import { TreeItem } from '@mui/lab'
import {
  Box,
  Checkbox,
  InputAdornment,
  OutlinedInput,
  Typography
} from '@mui/material'
import { Avatar, Icon, LinearProgressBar, TreeViewTooltip, TruncatedText } from 'components'
import { Tree, hierarchyIconIds } from 'stores/tree'
import { theme } from 'config'
import { format } from 'date-fns'
import { observer } from 'mobx-react'
import React, { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { HierarchyTypes, TreeDataItem } from 'types'
import { TreeContext } from './treeViewContainer'

export interface IContentTreeItemProps {
  depth:    number,
  item:     TreeDataItem,
  parent?:  TreeDataItem,
  icon?:    ReactNode,
  checked?: boolean,
}

export const ContentTreeItem = observer((props: IContentTreeItemProps): JSX.Element => {
  const { icon, depth, item, parent, checked } = props;
  const tree: Tree = React.useContext(TreeContext);
  const { t }     = useTranslation();

  const [searchString, setSearchString] = React.useState<string>('');
  const lastActivity = item.last_activity ? format(new Date(item.last_activity), 'MM/dd/yyyy HH:mm') : ''; 
  const isUser    = tree.hierarchy[depth] === HierarchyTypes.User;
  const isCompany = tree.hierarchy[depth] === HierarchyTypes.Company;
  const isQuiz    = tree.hierarchy[depth] === HierarchyTypes.Quiz;

  try {
    const children:         TreeDataItem[] = tree.getChildren(depth, item.tree_id);
    const filteredChildren: TreeDataItem[] = React.useMemo(() => {
      if (!searchString || !searchString.length) return children;
      return children.filter((c) =>
        c.label.toLowerCase().includes(searchString.toLowerCase())
      )
    }, [tree.data, tree.links, searchString, tree.hierarchy, checked, tree.is_loading]);

    const nodeId = parent ?  
      depth + '_' + parent.id + '_' + item.id
      : depth + '_' + item.id;

    if(!(tree.show_empty_tree || tree.hasFullDepth(depth, item.tree_id))) return null;

    if (children.length) // This is for tree items with children!
      return (
          <TreeItem
            icon   = {icon}
            sx     = {{ml: 2}}
            nodeId = {nodeId}
            label  = {
              <TreeViewTooltip item={item}>
                <Box sx={{ display: 'grid', gridAutoColumns: '1fr'}}>
                  <Box
                    sx             = {{gridColumn: 'span 2', gridRow: '1',}}
                    gap            = {2}
                    display        = 'flex'
                    flexDirection  = 'row'
                    alignItems     = 'center'
                  >
                    {isUser ?
                      <Avatar height='3.2rem' width='3.2rem' name={tree.show_fake_data ? t('fake_user_name') + ' ' + item.tree_id : item.label} hideEmail/> 
                      :
                      <Box flexDirection='row' display='flex' alignItems='center' gap={2} ml={2}>
                        <Icon 
                          size='size-18'
                          iconId={hierarchyIconIds[tree.hierarchy[depth]]}
                        />
                        <TruncatedText lines={1}>{tree.show_fake_data && isCompany ? t('fake_company_name') + ' ' + item.tree_id : item.label}</TruncatedText>
                      </Box>
                    }
                  </Box>
                  <Box  
                    alignItems     = 'center'
                    justifyContent = 'center'
                    display        = 'flex'
                    sx             = {{gridColumn: 'span 2', gridRow: '1', justifyContent: 'flext-start'}}
                  >
                    <Typography fontSize={theme.typography.fontSize}>{lastActivity}</Typography>
                  </Box>

                  <Box
                    alignItems     = 'center'
                    justifyContent = 'center'
                    display        = 'flex'
                    sx             = {{gridColumn: 'span 3', gridRow: '1',}}
                  >
                    {!isQuiz &&
                    <LinearProgressBar
                      label = {`${item.progress ? Math.round(item.progress) : 0}%`}
                      value = {item.progress || 0}
                      />
                    }
                  </Box>

                  <Box 
                    sx             = {{gridColumn: 'span 1', gridRow: '1',}}
                    display        = 'flex'
                    justifyContent = 'flex-end'
                  >
                  <Checkbox
                    checked  = {Boolean(checked)}
                    onChange = {() => {
                      tree.check(depth, item.tree_id);
                    }}
                    onClick  = {(e) => e.stopPropagation()}
                    />
                  </Box>
                </Box>
              </TreeViewTooltip>
            }
          >
            <Box sx = {{gridColumn: 'span 1', gridRow: '1',}} alignItems='center' display='flex' flexDirection='row'>
              <OutlinedInput
                sx             = {{ mb: 2, mt: 2, pl: 2, ml: 2 }}
                value          = {searchString}
                placeholder    ={t(tree.hierarchy[depth + 1])}
                onChange       = {(e) => setSearchString(e.target.value)}
                startAdornment = {
                  <InputAdornment position='start'>
                    <Icon
                      iconId={hierarchyIconIds[tree.hierarchy[depth + 1]]}
                    />
                  </InputAdornment>
                }
                endAdornment={
                  <InputAdornment position='end'>
                    <Icon iconId='search' />
                  </InputAdornment>
                }
              />
            </Box>
            {depth < tree.hierarchy.length &&
              filteredChildren.map((i) => (
                <ContentTreeItem
                  key     = {i.id}
                  depth   = {depth + 1}
                  item    = {i}
                  parent  = {item}
                  checked = {i.checked}
                />
              ))}
          </TreeItem>
      )
    else // This is for tree items without children
      return (
        <TreeViewTooltip item={item}>
          <TreeItem
            sx     = {{ml: 2}}
            nodeId = {nodeId}
            label  = {
              <Box sx={{ display: 'grid', gridAutoColumns: '1fr'}}>
                <Box
                  sx             = {{gridColumn: 'span 2', gridRow: '1'}}
                  gap            = {2}
                  display        = 'flex'
                  flexDirection  = 'row'
                  justifyContent = 'flex-start'
                  alignItems     = 'center'
                >
                    {isUser ?
                      <Avatar height='3.2rem' width='3.2rem' name={tree.show_fake_data ? t('fake_user_name') + ' ' + item.tree_id : item.label} hideEmail/> 
                      :
                      <Box flexDirection='row' display='flex' alignItems='center' gap={2} ml={2}>
                        <Icon
                          iconId={hierarchyIconIds[tree.hierarchy[depth]]}
                        />
                        <TruncatedText lines={1}>{tree.show_fake_data && isCompany ? t('fake_company_name') + ' ' + item.tree_id  : item.label}</TruncatedText>
                      </Box>
                    }
                  </Box>
                    
                  <Box  
                    alignItems = 'center'
                    justifyContent ='center'
                    display    = 'flex'
                    sx         = {{gridColumn: 'span 2', gridRow: '1', justifyContent: 'flex-start'}}
                  >
                    <Typography fontSize={theme.typography.fontSize}>{lastActivity}</Typography>
                  </Box>

                <Box  
                  alignItems = 'center'
                  display    = 'flex'
                  sx         = {{gridColumn: 'span 3', gridRow: '1',}}
                >
                  {!isQuiz &&
                  <LinearProgressBar
                    label = {`${item.progress ? Math.round(item.progress) : 0}%`}
                    value = {item.progress || 0}
                  />
                  }
                </Box>

                <Box display='flex' sx={{gridColumn: 'span 1', gridRow: '1',}} justifyContent='flex-end'>
                <Checkbox
                  checked  = {Boolean(checked)}
                  onChange = {() => {
                    tree.check(depth, item.tree_id);
                  }}
                  onClick  = {(e) => e.stopPropagation()}
                />
                </Box>
              </Box>
            }
          />
        </TreeViewTooltip>
      )
  } catch (err) {
    console.error('Failed to construct data tree', err)
    return null
  }
});
